import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilLine } from "lucide-react";
import { Modal } from "../../../components/ui/modal";
import { useToast } from "../../../components/ui/toast";
import { cn } from "../../../lib/utils";
import { updateContactBatch } from "../api";

interface BatchRenameButtonProps {
  batchId: string;
  name: string;
  tags?: string[];
  className?: string;
  showLabel?: boolean;
}

const normalizeTags = (value: string) => Array.from(new Set(
  value.split(",").map(tag => tag.trim()).filter(Boolean),
)).slice(0, 20);

export function BatchRenameButton({ batchId, name, tags = [], className, showLabel = false }: BatchRenameButtonProps) {
  const [open, setOpen] = useState(false);
  const [nextName, setNextName] = useState(name);
  const [nextTags, setNextTags] = useState(tags.join(", "));
  const queryClient = useQueryClient();
  const toast = useToast();
  const parsedTags = useMemo(() => normalizeTags(nextTags), [nextTags]);
  const valid = nextName.trim().length > 0;
  const unchanged = nextName.trim() === name && JSON.stringify(parsedTags) === JSON.stringify(tags);

  useEffect(() => {
    if (!open) return;
    setNextName(name);
    setNextTags(tags.join(", "));
  }, [open, name, tags]);

  const update = useMutation({
    mutationFn: () => updateContactBatch(batchId, { name: nextName.trim(), tags: parsedTags }),
    onSuccess: (updated: any) => {
      const patch = { name: updated.name, tags: updated.tags || [] };
      queryClient.setQueryData<any[]>(["contact-batches"], (batches = []) =>
        batches.map((batch) => batch.batch_id === batchId ? { ...batch, ...patch } : batch),
      );
      queryClient.setQueryData<any>(["contact-batch", batchId], (detail: any) =>
        detail ? { ...detail, batch: { ...detail.batch, ...patch } } : detail,
      );
      void queryClient.invalidateQueries({ queryKey: ["contact-batches"] });
      void queryClient.invalidateQueries({ queryKey: ["contact-batch", batchId] });
      setOpen(false);
      toast.success("Batch details updated");
    },
    onError: (error: any) => toast.danger("Couldn't update batch", error.message),
  });

  const close = () => { if (!update.isPending) setOpen(false); };
  const save = () => {
    if (!valid || update.isPending) return;
    if (unchanged) { setOpen(false); return; }
    update.mutate();
  };

  return (
    <>
      <button
        type="button"
        title="Edit batch name and tags"
        aria-label={`Edit batch ${name}`}
        onClick={(event) => { event.stopPropagation(); setOpen(true); }}
        disabled={update.isPending}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-lg p-2 text-ink-3 transition-colors hover:bg-soft hover:text-ink disabled:opacity-50",
          showLabel && "px-3 text-xs font-semibold",
          className,
        )}
      >
        <PencilLine className="h-4 w-4" />
        {showLabel && "Edit"}
      </button>
      {open && (
        <Modal
          title="Edit batch"
          onClose={close}
          width="max-w-sm"
          footer={<>
            <button onClick={close} disabled={update.isPending} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-50">Cancel</button>
            <button onClick={save} disabled={!valid || update.isPending} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50">
              {update.isPending ? "Saving…" : "Save"}
            </button>
          </>}
        >
          <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); save(); }}>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Batch name</span>
              <input autoFocus aria-label="Batch name" value={nextName} maxLength={120} onChange={event => setNextName(event.target.value)} className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30" />
              {!valid && <span className="mt-1 block text-xs text-danger">Name is required.</span>}
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Tags</span>
              <input aria-label="Tags" value={nextTags} onChange={event => setNextTags(event.target.value)} placeholder="e.g. priority, diwali" className="w-full rounded-lg border border-line px-3 py-2.5 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30" />
              <span className="mt-1 block text-xs text-ink-3">Comma separated, up to 20 tags.</span>
            </label>
            <button type="submit" hidden />
          </form>
        </Modal>
      )}
    </>
  );
}
